import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { resultAPI, leaderboardAPI } from '@/lib/api';
import { formatTime, formatDate, cn } from '@/lib/utils';
import { sanitizeHTML } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BookOpen, Trophy, Clock, CheckCircle, XCircle,
  ArrowLeft, Medal, ChevronDown, ChevronUp, Mail, FileDown, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// Helper to render HTML content
const renderHTML = (html) => {
  if (!html) return null;
  const isPlainText = !html.includes('<') && !html.includes('>');
  if (isPlainText) return html;
  return <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(html) }} />;
};

export default function ResultPage() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    loadResult();
  }, [resultId]);

  const loadResult = async () => {
    try {
      const res = await resultAPI.getOne(resultId);
      setResult(res.data);
      
      // Load leaderboard
      const lbRes = await leaderboardAPI.get(res.data.tryout_id);
      setLeaderboard(lbRes.data);
    } catch (err) {
      console.error('Failed to load result:', err);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await resultAPI.sendEmail(resultId);
      toast.success('Email hasil ujian berhasil dikirim!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info('Mengunduh PDF...');
      
      // Get token for auth
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Fetch PDF with authorization header
      const response = await fetch(`${backendUrl}/api/results/${resultId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Hasil_TryOut_${result?.tryout_title || 'exam'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF berhasil diunduh!');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Gagal mengunduh PDF');
    }
  };

  const getGradeInfo = (percentage) => {
    if (percentage >= 90) return { grade: 'A', color: 'text-green-600', bg: 'bg-green-50', message: 'Luar Biasa!' };
    if (percentage >= 80) return { grade: 'B+', color: 'text-green-600', bg: 'bg-green-50', message: 'Sangat Baik!' };
    if (percentage >= 70) return { grade: 'B', color: 'text-blue-600', bg: 'bg-blue-50', message: 'Baik!' };
    if (percentage >= 60) return { grade: 'C+', color: 'text-yellow-600', bg: 'bg-yellow-50', message: 'Cukup Baik' };
    if (percentage >= 50) return { grade: 'C', color: 'text-yellow-600', bg: 'bg-yellow-50', message: 'Cukup' };
    return { grade: 'D', color: 'text-red-600', bg: 'bg-red-50', message: 'Perlu Ditingkatkan' };
  };

  const toggleQuestion = (qId) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(qId)) {
        newSet.delete(qId);
      } else {
        newSet.add(qId);
      }
      return newSet;
    });
  };

  const getUserAnswer = (questionId) => {
    const answer = result?.answers?.find(a => a.question_id === questionId);
    return answer?.selected_option_id;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0047AB] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Memuat hasil...</p>
        </div>
      </div>
    );
  }

  const gradeInfo = getGradeInfo(result?.percentage || 0);
  const userRank = leaderboard.findIndex(l => l.user_name === result?.user_name) + 1;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="result-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Kembali
              </Button>
            </Link>
            <div className="w-10 h-10 bg-[#0047AB] rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">{result?.tryout_title}</h1>
              <p className="text-sm text-slate-500">
                Hasil Try Out 
                {result?.attempt && <span className="ml-1 text-blue-600 font-medium">(Percobaan ke-{result.attempt})</span>}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Show retry button if this is 1st attempt */}
            {result?.attempt === 1 && (
              <Link to={`/exam/${result?.tryout_id}`}>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                  data-testid="retry-exam-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Ulangi (1x lagi)
                </Button>
              </Link>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSendEmail}
              disabled={sendingEmail}
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Kirim Email
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleExportPdf}
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Score Card */}
        <Card className="mb-8 overflow-hidden">
          <div className={cn("p-8", gradeInfo.bg)}>
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="text-center">
                <div className={cn("text-8xl font-bold", gradeInfo.color)}>{gradeInfo.grade}</div>
                <div className="text-lg text-slate-600 mt-2">{gradeInfo.message}</div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white/80 rounded-xl p-4">
                  <div className="text-sm text-slate-500">Skor Anda</div>
                  <div className="text-3xl font-bold text-slate-900">
                    {result?.score?.toFixed(1) || 0} <span className="text-lg font-normal text-slate-500">/ {result?.total_points?.toFixed(1) || 0}</span>
                  </div>
                </div>
                <div className="bg-white/80 rounded-xl p-4">
                  <div className="text-sm text-slate-500">Jawaban Benar</div>
                  <div className="text-3xl font-bold text-slate-900">
                    {result?.correct_count}/{result?.total_questions}
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white/80 rounded-xl p-4">
                  <div className="text-sm text-slate-500">Waktu Pengerjaan</div>
                  <div className="text-3xl font-bold font-mono text-slate-900">
                    {formatTime(result?.time_spent_seconds || 0)}
                  </div>
                </div>
                <div className="bg-white/80 rounded-xl p-4">
                  <div className="text-sm text-slate-500">Peringkat Anda</div>
                  <div className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                    {userRank > 0 && userRank <= 3 && (
                      <Medal className={cn("w-8 h-8", 
                        userRank === 1 ? "text-yellow-500" : 
                        userRank === 2 ? "text-slate-400" : "text-amber-600"
                      )} />
                    )}
                    #{userRank || '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="review" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
            <TabsTrigger 
              value="review" 
              className="rounded-lg data-[state=active]:bg-[#0047AB] data-[state=active]:text-white"
              data-testid="tab-review"
            >
              Pembahasan
            </TabsTrigger>
            <TabsTrigger 
              value="leaderboard" 
              className="rounded-lg data-[state=active]:bg-[#0047AB] data-[state=active]:text-white"
              data-testid="tab-leaderboard"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Leaderboard
            </TabsTrigger>
          </TabsList>

          {/* Review Tab */}
          <TabsContent value="review">
            <div className="space-y-4">
              {result?.questions?.map((question, index) => {
                const userAnswer = getUserAnswer(question.id);
                const correctOption = question.options.find(o => o.is_correct);
                const isCorrect = userAnswer === correctOption?.id;
                const isExpanded = expandedQuestions.has(question.id);
                
                return (
                  <Card 
                    key={question.id} 
                    className={cn(
                      "transition-all",
                      isCorrect ? "border-green-200" : "border-red-200"
                    )}
                    data-testid={`review-question-${index}`}
                  >
                    <CardContent className="p-6">
                      <div 
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => toggleQuestion(question.id)}
                      >
                        <div className="flex items-start gap-4">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                            isCorrect ? "bg-green-100" : "bg-red-100"
                          )}>
                            {isCorrect ? (
                              <CheckCircle className="w-6 h-6 text-green-600" />
                            ) : (
                              <XCircle className="w-6 h-6 text-red-600" />
                            )}
                          </div>
                          <div>
                            <Badge variant="secondary" className="mb-2">Soal {index + 1}</Badge>
                            <div className="text-slate-900 prose prose-sm max-w-none">
                              {renderHTML(question.text)}
                            </div>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      
                      {isExpanded && (
                        <div className="mt-6 ml-14 space-y-4">
                          {/* Options */}
                          <div className="space-y-2">
                            {question.options.map((opt, i) => (
                              <div 
                                key={opt.id}
                                className={cn(
                                  "p-3 rounded-lg border",
                                  opt.is_correct 
                                    ? "bg-green-50 border-green-300"
                                    : userAnswer === opt.id
                                      ? "bg-red-50 border-red-300"
                                      : "bg-slate-50 border-slate-200"
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <span className="font-medium text-slate-600">
                                    {String.fromCharCode(65 + i)}.
                                  </span>
                                  <span className="flex-1">{renderHTML(opt.text)}</span>
                                  {opt.is_correct && (
                                    <Badge className="ml-auto bg-green-100 text-green-700 hover:bg-green-100">
                                      Benar
                                    </Badge>
                                  )}
                                  {userAnswer === opt.id && !opt.is_correct && (
                                    <Badge className="ml-auto bg-red-100 text-red-700 hover:bg-red-100">
                                      Jawaban Anda
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Explanation */}
                          {question.explanation && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h4 className="font-semibold text-blue-900 mb-2">Pembahasan:</h4>
                              <div className="text-blue-800 prose prose-sm max-w-none">
                                {renderHTML(question.explanation)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Papan Peringkat
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {leaderboard.map((entry, i) => (
                    <div 
                      key={i}
                      className={cn(
                        "flex items-center justify-between p-4 rounded-xl",
                        entry.user_name === result?.user_name 
                          ? "bg-[#0047AB]/10 border-2 border-[#0047AB]" 
                          : "bg-slate-50"
                      )}
                      data-testid={`leaderboard-entry-${i}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center font-bold",
                          entry.rank === 1 ? "bg-yellow-100 text-yellow-700" :
                          entry.rank === 2 ? "bg-slate-200 text-slate-700" :
                          entry.rank === 3 ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-600"
                        )}>
                          {entry.rank <= 3 ? (
                            <Medal className={cn("w-5 h-5",
                              entry.rank === 1 ? "text-yellow-500" :
                              entry.rank === 2 ? "text-slate-400" :
                              "text-amber-600"
                            )} />
                          ) : (
                            entry.rank
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">
                            {entry.user_name}
                            {entry.user_name === result?.user_name && (
                              <Badge className="ml-2 bg-[#0047AB] text-white">Anda</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="text-right">
                          <div className="text-sm text-slate-500">Skor</div>
                          <div className="font-bold text-slate-900">{entry.percentage}%</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-slate-500">Waktu</div>
                          <div className="font-mono text-slate-900">{formatTime(entry.time_spent_seconds)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Link to="/dashboard">
            <Button variant="outline" size="lg" className="rounded-full px-8" data-testid="back-to-dashboard-btn">
              Kembali ke Dashboard
            </Button>
          </Link>
          <Link to={`/exam/${result?.tryout_id}`}>
            <Button size="lg" className="bg-[#0047AB] hover:bg-[#0047AB]/90 rounded-full px-8" data-testid="retry-exam-btn">
              Coba Lagi
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
